import webpush from "web-push";
import type pino from "pino";
import type { WebPushStore, WebPushEntry } from "./web-push-store.js";

export interface WebPushPayload {
  title: string;
  body: string;
  data?: Record<string, unknown>;
}

interface WebPushSubLike {
  endpoint: string;
  keys: { p256dh: string; auth: string };
}

/**
 * Service for sending Web Push notifications to browser/PWA subscribers.
 *
 * Endpoints that respond 404/410/403 are considered dead and are removed
 * from the store on the spot.
 *
 * Per-event preference filtering happens upstream — callers fetch subs via
 * `webPushStore.getSubsForEvent(eventId)` and pass the filtered list here.
 */
export class WebPushService {
  private readonly logger: pino.Logger;
  private readonly store: WebPushStore;

  constructor(logger: pino.Logger, store: WebPushStore) {
    this.logger = logger.child({ component: "web-push-service" });
    this.store = store;
  }

  async sendPush(subs: WebPushSubLike[], payload: WebPushPayload): Promise<void> {
    if (subs.length === 0) return;

    const json = JSON.stringify(payload);
    const vapidDetails = this.store.getVapidDetails();

    await Promise.all(
      subs.map(async (sub) => {
        try {
          await webpush.sendNotification(sub, json, { vapidDetails });
        } catch (error) {
          const status =
            error && typeof error === "object" && "statusCode" in error
              ? (error as { statusCode?: number }).statusCode
              : undefined;
          if (status === 404 || status === 410 || status === 403) {
            this.logger.info({ endpoint: sub.endpoint, status }, "Pruning dead web push endpoint");
            this.store.remove(sub.endpoint);
            return;
          }
          this.logger.warn({ err: error, endpoint: sub.endpoint }, "Web push send failed");
        }
      }),
    );
  }

  /**
   * Bypasses per-event filtering — fires to every persisted subscription.
   * Used by the "Send test notification" button.
   */
  async sendPushToAll(payload: WebPushPayload): Promise<void> {
    await this.sendPush(this.store.getAll(), payload);
  }

  /**
   * Best-effort silent validation push, used right after a new subscription
   * is registered. Subscribers' service workers must drop `type: "test"`
   * payloads without showing a notification.
   */
  async validate(sub: WebPushSubLike): Promise<void> {
    try {
      await webpush.sendNotification(sub, JSON.stringify({ type: "test" }), {
        TTL: 0,
        vapidDetails: this.store.getVapidDetails(),
      });
    } catch (error) {
      const status =
        error && typeof error === "object" && "statusCode" in error
          ? (error as { statusCode?: number }).statusCode
          : undefined;
      if (status === 404 || status === 410 || status === 403) {
        this.store.remove(sub.endpoint);
      }
    }
  }

  /** For diagnostics. */
  describeEntries(): WebPushEntry[] {
    return this.store.getAllEntries();
  }
}
